import type { FlowDefinition, FlowNode } from '../types';

function toPortalLinkTag(index: number) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let nextIndex = index;
  let label = '';
  do {
    label = alphabet[nextIndex % alphabet.length] + label;
    nextIndex = Math.floor(nextIndex / alphabet.length) - 1;
  } while (nextIndex >= 0);
  return `Link ${label}`;
}

function getConfiguredPortalName(node: FlowNode | null | undefined) {
  const configuredName =
    typeof node?.config?.portalName === 'string' ? node.config.portalName.trim() : '';
  return configuredName || 'Global Portal';
}

export type GlobalPortalTargetOption = {
  value: string;
  flowId: string;
  nodeId: string;
  label: string;
  portalName: string;
  linkTag: string;
  sectionId?: string | null;
};

export function encodeGlobalPortalTargetValue(flowId: string, nodeId: string) {
  return `${flowId}::${nodeId}`;
}

export function decodeGlobalPortalTargetValue(value: string | null | undefined) {
  if (typeof value !== 'string' || !value.includes('::')) return null;
  const [flowId, nodeId] = value.split('::');
  if (!flowId || !nodeId) return null;
  return { flowId, nodeId };
}

function getSortedPortalSenders(flows: FlowDefinition[] | null | undefined) {
  if (!Array.isArray(flows)) return [] as Array<{ flow: FlowDefinition; node: FlowNode }>;
  return flows.flatMap((flow) =>
    flow.nodes
      .filter((node) => node.blockType === 'global-portal')
      .map((node) => ({ flow, node }))
  );
}

export function buildGlobalPortalLinkTagMap(flows: FlowDefinition[] | null | undefined) {
  const tagMap = new Map<string, string>();
  getSortedPortalSenders(flows).forEach(({ flow, node }, index) => {
    tagMap.set(encodeGlobalPortalTargetValue(flow.id, node.id), toPortalLinkTag(index));
  });
  return tagMap;
}

export function buildGlobalPortalTargetOptions(args: {
  flows: FlowDefinition[] | null | undefined;
  currentNodeId?: string | null;
}) {
  const { flows, currentNodeId } = args;
  const linkTagMap = buildGlobalPortalLinkTagMap(flows);
  return getSortedPortalSenders(flows)
    .filter(({ node }) => node.id !== currentNodeId)
    .map(({ flow, node }) => {
      const value = encodeGlobalPortalTargetValue(flow.id, node.id);
      const portalName = getConfiguredPortalName(node);
      const linkTag = linkTagMap.get(value) ?? 'Link';
      return {
        value,
        flowId: flow.id,
        nodeId: node.id,
        label: `[${linkTag}] ${portalName}`,
        portalName,
        linkTag,
        sectionId: flow.sectionId ?? null,
      } satisfies GlobalPortalTargetOption;
    });
}

export function resolveGlobalPortalTarget(
  flows: FlowDefinition[] | null | undefined,
  node: FlowNode | null | undefined
) {
  if (!Array.isArray(flows) || !node) return null;

  if (node.blockType === 'portal-receiver') {
    const configuredFlowId =
      typeof node.config?.senderFlowId === 'string' ? node.config.senderFlowId : '';
    const configuredNodeId =
      typeof node.config?.senderNodeId === 'string' ? node.config.senderNodeId : '';
    const decodedTarget =
      decodeGlobalPortalTargetValue(
        typeof node.config?.senderRef === 'string' ? node.config.senderRef : null
      ) ?? null;

    const flowId = configuredFlowId || decodedTarget?.flowId || '';
    const nodeId = configuredNodeId || decodedTarget?.nodeId || '';
    if (!flowId || !nodeId) return null;

    const targetFlow = flows.find((entry) => entry.id === flowId) ?? null;
    const targetNode =
      targetFlow?.nodes.find(
        (entry) => entry.id === nodeId && entry.blockType === 'global-portal'
      ) ?? null;
    if (!targetFlow || !targetNode) return null;

    return {
      flow: targetFlow,
      node: targetNode,
      linkTag:
        buildGlobalPortalLinkTagMap(flows).get(encodeGlobalPortalTargetValue(flowId, nodeId)) ??
        null,
      portalName: getConfiguredPortalName(targetNode),
    };
  }

  if (node.blockType === 'global-portal') {
    return {
      flow:
        flows.find((entry) => entry.nodes.some((candidate) => candidate.id === node.id)) ?? null,
      node,
      linkTag:
        buildGlobalPortalLinkTagMap(flows).get(
          encodeGlobalPortalTargetValue(
            flows.find((entry) => entry.nodes.some((candidate) => candidate.id === node.id))?.id ??
              '',
            node.id
          )
        ) ?? null,
      portalName: getConfiguredPortalName(node),
    };
  }

  return null;
}

export function getGlobalPortalConnectedSocketIds(node: FlowNode | null | undefined) {
  const connected = new Set<string>();
  (node?.inputs ?? []).forEach((binding) => {
    if (
      binding.socketId === 'selection' ||
      binding.socketId === 'item' ||
      binding.socketId === 'items' ||
      binding.socketId === 'value' ||
      binding.socketId === 'trigger'
    ) {
      connected.add(binding.socketId);
    }
  });
  return connected;
}

export function getConfiguredPortalNameForNode(node: FlowNode | null | undefined) {
  return getConfiguredPortalName(node);
}

export function findPortalReceiversForSender(
  flows: FlowDefinition[] | null | undefined,
  senderFlowId: string,
  senderNodeId: string
) {
  if (!Array.isArray(flows)) return [] as Array<{ flow: FlowDefinition; node: FlowNode }>;
  const senderRef = encodeGlobalPortalTargetValue(senderFlowId, senderNodeId);
  return flows.flatMap((flow) =>
    flow.nodes
      .filter((node) => {
        if (node.blockType !== 'portal-receiver') return false;
        const configuredSenderFlowId =
          typeof node.config?.senderFlowId === 'string' ? node.config.senderFlowId : '';
        const configuredSenderNodeId =
          typeof node.config?.senderNodeId === 'string' ? node.config.senderNodeId : '';
        const configuredSenderRef =
          typeof node.config?.senderRef === 'string' ? node.config.senderRef : '';
        return (
          configuredSenderRef === senderRef ||
          (configuredSenderFlowId === senderFlowId &&
            configuredSenderNodeId === senderNodeId)
        );
      })
      .map((node) => ({ flow, node }))
  );
}
